// Extended types for Visit with full context and form state

export interface Animal {
  id: string;
  name: string;
  species: string;
  breed?: string;
  gender?: string;
  dateOfBirth?: string;
  age?: number;
  weight?: number;
  microchip?: string;
  allergies?: string[];
  flags?: string[];
}

export interface Owner {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  communicationPreferences?: {
    sms: boolean;
    email: boolean;
    whatsapp: boolean;
  };
}

export interface ServiceItem {
  id: string;
  name: string;
  type: 'consultation' | 'vaccination' | 'surgery' | 'diagnostic' | 'treatment' | 'other';
  duration?: number;
  price?: number;
}

export type VisitStatus =
  | 'planned'
  | 'draft'
  | 'in_progress'
  | 'signed'
  | 'locked'
  | 'completed'
  | 'cancelled';
export type VisitDocumentStatus = 'draft' | 'signed' | 'locked';

export interface VisitWithContext {
  id: string;
  clinicId: string;
  petId: string;
  ownerId: string;
  doctorId: string;
  appointmentId?: string;
  
  // Status workflow
  status: VisitStatus;
  documentStatus: VisitDocumentStatus;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  arrivedAt?: string;
  startedAt?: string;
  endedAt?: string;
  signedAt?: string;
  signedBy?: string;
  lockedAt?: string;
  
  // QR gating
  qrRedeemedAt?: string;
  qrRedeemedBy?: string;
  
  // Context
  animal?: Animal;
  owner?: Owner;
  services: ServiceItem[];
  
  // Clinical content (from form)
  visitType?: string;
  visitDate?: string;
  chiefComplaint?: string;
  
  // Form fields stored in backend
  vitals?: VitalsData;
  anamnesis?: AnamnesisData;
  exam?: ExamData;
  problems?: ProblemData[];
  plan?: PlanData;
  vaccination?: VaccinationData;
  treatments?: TreatmentData[];
  prescriptions?: PrescriptionData[];
  attachments?: AttachmentData[];
  billingItems?: BillingItemData[];
  
  notes?: string;
  recommendations?: string;
  signatureNotes?: string;
  
  // Computed
  duration?: number; // minutes
  cost?: number;
}

export interface VitalsData {
  temperature?: number;
  heartRate?: number;
  respiratoryRate?: number;
  crt?: number;
  painScore?: number;
  bcs?: number;
  mucosa?: string;
  weight?: number;
  recordedAt?: string;
}

export interface AnamnesisData {
  nutrition?: string;
  deworming?: string;
  dewormingLast?: string;
  currentMeds?: string;
  allergies?: string;
  environment?: string;
  reproduction?: string;
}

export interface ExamData {
  allNormal?: boolean;
  notes?: string;
  systems?: Record<string, 'normal' | 'abnormal' | 'not_examined'>;
  systemNotes?: Record<string, string>;
}

export interface ProblemData {
  id: string;
  description: string;
  severity?: 'mild' | 'moderate' | 'severe' | 'critical';
  type?: 'definitive' | 'presumptive' | 'differential';
  resolved?: boolean;
  icdCode?: string;
}

export interface PlanData {
  investigations?: string;
  treatments?: string;
  homeCare?: string;
  recallDate?: string;
  recallReason?: string;
  recallCreated?: boolean;
}

export interface VaccinationData {
  product?: string;
  dose?: string;
  doseAmount?: number;
  doseUnit?: string;
  site?: string;
  producer?: string;
  lot: string; // Required for legal compliance
  exp: string; // Required for legal compliance
  scheme?: 'primar' | 'rapel' | 'booster';
  nextDue?: string;
  adverse?: string;
  administeredAt?: string;
  administeredBy?: string;
}

export interface TreatmentData {
  id: string;
  drug: string;
  drugId?: string; // Link to inventory
  dose: string;
  doseAmount?: number;
  doseUnit?: string;
  route: 'oral' | 'iv' | 'im' | 'sc' | 'topical' | 'other';
  time: string;
  qty: number;
  uom: string;
  lot?: string;
  exp?: string;
  fromStock: boolean;
  stockConsumed?: boolean;
}

export interface PrescriptionData {
  id: string;
  drug: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions: string;
  qty: number;
  uom: string;
}

export interface AttachmentData {
  id: string;
  name: string;
  url: string;
  cdnUrl?: string;
  type: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
}

export interface BillingItemData {
  id: string;
  itemId?: string; // Link to inventory/services
  name: string;
  qty: number;
  uom: string;
  unitPrice: number;
  total: number;
  lot?: string;
  exp?: string;
  fromStock: boolean;
  taxRate?: number;
}

// Form state (mutable in UI, synced to backend)
export interface VisitFormState {
  vitals: VitalsData;
  anamnesis: AnamnesisData;
  exam: ExamData;
  problems: ProblemData[];
  plan: PlanData;
  vaccination: VaccinationData | null;
  treatments: TreatmentData[];
  prescriptions: PrescriptionData[];
  billingItems: BillingItemData[];
  notes: string;
  recommendations: string;
  ownerMessage: string;
  ownerMessageChannel: 'sms' | 'email' | 'whatsapp';
  publicNote: boolean;
}

export const createEmptyFormState = (): VisitFormState => ({
  vitals: {},
  anamnesis: {},
  exam: { systems: {} },
  problems: [],
  plan: {},
  vaccination: null,
  treatments: [],
  prescriptions: [],
  billingItems: [],
  notes: '',
  recommendations: '',
  ownerMessage: '',
  ownerMessageChannel: 'sms',
  publicNote: false
});

